import { format, isSameDay, isValid, parse, startOfDay, subDays } from "date-fns";

/**
 * `created:` values:
 *   today | -N | N           created N days ago or later
 *   YYYY-MM-DD               created on or after that day
 *   YYYY-MM-DD..YYYY-MM-DD   created within the range (either end may be left out)
 */

const API_DAY = "yyyy-MM-dd";
const RELATIVE_DAYS = /^-?\d+$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

export const CREATED_PRESETS = [
  { value: "today", label: "Today" },
  { value: "-7", label: "Last 7 days" },
  { value: "-30", label: "Last 30 days" },
  { value: "-90", label: "Last 90 days" },
] as const;

/** Shaped like react-day-picker's DateRange, so a parsed value can drive the calendar. */
interface CreatedRange {
  from: Date | undefined;
  to?: Date;
}

function parseDay(text: string): Date | undefined {
  if (!DAY.test(text)) return undefined;
  const day = parse(text, API_DAY, new Date());
  return isValid(day) ? day : undefined;
}

function relativeDays(value: string): number | undefined {
  if (value.toLowerCase() === "today") return 0;
  return RELATIVE_DAYS.test(value) ? Math.abs(parseInt(value, 10)) : undefined;
}

export function parseCreated(value: string | undefined): CreatedRange | undefined {
  if (!value) return undefined;
  const days = relativeDays(value);
  if (days !== undefined) return { from: startOfDay(subDays(new Date(), days)) };

  const [fromText, toText, ...rest] = value.split("..");
  if (rest.length) return undefined;
  if (toText === undefined) {
    const from = parseDay(fromText);
    return from && { from };
  }
  const range = { from: parseDay(fromText), to: parseDay(toText) };
  const malformed = (fromText && !range.from) || (toText && !range.to);
  return malformed || (!range.from && !range.to) ? undefined : range;
}

export function createdParams(value: string | undefined) {
  const range = parseCreated(value);
  return {
    ...(range?.from && { created_after: format(range.from, API_DAY) }),
    ...(range?.to && { created_before: format(range.to, API_DAY) }),
  };
}

/** The `created:` value for a picked calendar range. */
export function createdRangeValue({ from, to }: { from: Date; to?: Date }): string {
  if (!to || isSameDay(from, to)) return format(from, API_DAY);
  return `${format(from, API_DAY)}..${format(to, API_DAY)}`;
}

/** How a `created:` value reads in the filters sheet. */
export function describeCreated(value: string): string {
  const preset = CREATED_PRESETS.find((p) => p.value === value);
  if (preset) return preset.label;

  const days = relativeDays(value);
  if (days !== undefined) return days === 0 ? "Today" : `Last ${days} days`;

  const range = parseCreated(value);
  if (!range) return value;
  const { from, to } = range;
  if (from && !to && value.includes("..")) return `Since ${format(from, "MMM d, yyyy")}`;
  if (from && !to) return format(from, "MMM d, yyyy");
  if (!from && to) return `Until ${format(to, "MMM d, yyyy")}`;
  return `${format(from!, "MMM d")} – ${format(to!, "MMM d, yyyy")}`;
}
